/**
 * VendorOutreachHistory — read-only timeline of past outreach events on
 * a single vendor. Renders compactly so it can sit beside the composer.
 */
import React, { useEffect, useState } from 'react';
import { API } from '@/App';
import { Mail, Megaphone, Send, Loader2 } from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';

const channelIcon = (c) => c === 'whatsapp' ? Megaphone : c === 'email' ? Mail : Send;
const channelTint = (c) => c === 'whatsapp' ? 'text-emerald-700' : c === 'email' ? 'text-sky-700' : 'text-amber-700';

export default function VendorOutreachHistory({ vendorId, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendorId) return;
    setLoading(true);
    const t = localStorage.getItem('session_token');
    fetch(`${API}/pollen/outreach?vendor_id=${encodeURIComponent(vendorId)}`, {
      credentials: 'include',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then(r => r.ok ? r.json() : [])
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [vendorId, refreshKey]);

  if (loading) return <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Loading outreach…</div>;
  if (!rows.length) return <p className="text-xs text-muted-foreground">No outreach logged yet for this vendor.</p>;

  return (
    <div className="space-y-2" data-testid="vendor-outreach-history">
      {rows.slice(0, 10).map(r => {
        const Icon = channelIcon(r.channel);
        return (
          <div key={r.id} className="text-xs flex items-start gap-2 p-2 rounded border bg-muted/20">
            <Icon className={`w-3.5 h-3.5 mt-0.5 ${channelTint(r.channel)}`} />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="font-medium capitalize">{r.channel}</span>
                <span className="text-muted-foreground">→ {r.recipient || '—'}</span>
                <span className="text-muted-foreground">· {formatDate(r.sent_at)}</span>
                {r.query_id && <span className="text-amber-700">· Query</span>}
              </div>
              {r.subject && <div className="text-[11px] text-muted-foreground truncate">📧 {r.subject}</div>}
              <div className="text-[11px] text-foreground/80 line-clamp-2 whitespace-pre-line">{r.message}</div>
              <div className="text-[10px] text-muted-foreground/80 mt-0.5">by {r.sent_by_name || r.sent_by}</div>
            </div>
          </div>
        );
      })}
      {rows.length > 10 && (
        <p className="text-[11px] text-muted-foreground">+ {rows.length - 10} earlier outreach event(s)</p>
      )}
    </div>
  );
}
